import { useState } from 'react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useSubmitReport, REPORT_TYPES, ReportType } from '@/hooks/useReports';
import { containsProfanity } from '@/lib/profanityFilter';
import { toast } from '@/lib/toast';

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportedUserId?: string;
  reportedItemId?: string;
  targetLabel?: string;
}

export function ReportDialog({
  open,
  onOpenChange,
  reportedUserId,
  reportedItemId,
  targetLabel = 'this content',
}: ReportDialogProps) {
  const [type, setType] = useState<ReportType | ''>('');
  const [description, setDescription] = useState('');
  const submitReport = useSubmitReport();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!type) return;

    if (containsProfanity(description)) {
      toast.error('Please use appropriate language in your report');
      return;
    }

    await submitReport.mutateAsync({
      reportedUserId,
      reportedItemId,
      type,
      description: description.trim(),
    });

    setType('');
    setDescription('');
    onOpenChange(false);
  };

  const isValid = type && description.trim().length >= 20;

  return (
    <GlassModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Report ${targetLabel}`}
      description="Help us keep the community safe. Describe the issue you've encountered."
    >
      <div className="space-y-1 text-left">
        <h2 className="text-lg font-semibold text-foreground">Report {targetLabel}</h2>
        <p className="text-sm text-muted-foreground">
          Help us keep the community safe. Describe the issue you've encountered.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 pt-4">
        <div className="space-y-2">
          <Label htmlFor="report-type">Type of Issue</Label>
          <Select value={type} onValueChange={(v) => setType(v as ReportType)}>
            <SelectTrigger id="report-type">
              <SelectValue placeholder="Select a reason..." />
            </SelectTrigger>
            <SelectContent>
              {REPORT_TYPES.map((rt) => (
                <SelectItem key={rt.value} value={rt.value}>
                  {rt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">
            Description <span className="text-muted-foreground text-xs">(min 20 characters)</span>
          </Label>
          <Textarea
            id="description"
            placeholder="Please describe the issue in detail..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={1000}
          />
          <p className="text-xs text-muted-foreground text-right">
            {description.length}/1000
          </p>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            False or malicious reports may result in action against your account.
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitReport.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="destructive"
            disabled={!isValid || submitReport.isPending}
          >
            {submitReport.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </Button>
        </div>
      </form>
    </GlassModal>
  );
}
